import {
  Grid,
  CircularProgress,
  Typography,
  FormControl,
  OutlinedInput,
} from "@mui/material";
import { API, DATEFORMATS } from "../../../globals/globals";
import {
  useExportAlarmsMutation,
  useExportDisconnectedRadioNodesMutation,
  useGetAlarmsByDeviceKeyMutation,
  useGetDrawerCountsMutation,
  useGetRadioNodeQuery,
} from "../../../store/api/devices/deviceApiSlice";
import CustomDataGrid from "../../../components/Shared/Table/CustomDataGrid";
import { disconnectedRadioNodeColumns } from "../../../components/Shared/Table/Columns";
import { IEvent } from "../../../store/api/devices/types";
import { useEffect, useMemo, useState } from "react";
import { convertEpochToGMT, errorMessage, exportInCSV, getSortBy } from "../../../utils/helpers";
import { useStoreDispatch } from "../../../store/hooks";
import { setIsLoadingStatus, setOpenToast, setPaginationModel } from "../../../store/helpers/helperReducers";
import {
  GridEventListener,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid";
import EventDrawer from "../../../components/Drawers/EventDrawer";
import { useSelector } from "react-redux";
import { paginationModelSelector } from "../../../store/helpers/helperSelectors";
import { useDebounce } from "../hooks";
import DeviceDrawer from "../../../components/Drawers/DeviceDrawer";
import { Loader2 } from "lucide-react";
import { searchKeywordSelector } from "../../../store/search/selector";
import { SortingState } from "@tanstack/react-table";
import { useGetDeviceFullDetailMutation } from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import { setDrawerCounts, setOpenDeviceDrawer, setSelectedDevice, setSelectedDeviceAlarmCount, setSelectedDeviceAlarms } from "../../../store/api/devices/reducers";
import { Button } from "../../../components/ui/button";
import { Icon } from "../../../components/Shared/Icon";
import SearchIcon from "@mui/icons-material/Search";

interface DisconnectedRadioNodeProps {
  selectedGeoRegions: string[];
}

interface ToolbarProps {
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  exportHandler: () => void;
  exportLoading: boolean;
}

const DataGridToolbar: React.FC<ToolbarProps> = ({
  searchText,
  setSearchText,
  exportHandler,
  exportLoading,
}) => {
  return (
    <div className="flex items-center justify-between">
      <div className="relative w-80">
        <SearchIcon
          sx={{ fontSize: 18 }}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
        />
        <input
          type="text"
          placeholder="Search radio nodes..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-input rounded-md
                     focus:outline-none focus:ring-1 focus:ring-ring
                     focus:border-ring transition duration-200"
          style={{
            backgroundColor: "var(--card)",
            color: "var(--foreground)",
            fontSize: "0.875rem",
            height: 36,
          }}
        />
      </div>
      <Button
        variant="outline"
        size="icon"
        className="h-9 w-9 shrink-0"
        disabled={exportLoading}
        onClick={exportHandler}
        aria-label="Export CSV"
      >
        {exportLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Icon name="download" size={18} />
        )}
      </Button>
    </div>
  );
};

const DisconnectedRadioNode: React.FC<DisconnectedRadioNodeProps> = ({ selectedGeoRegions }) => {
  const keyword = useSelector(searchKeywordSelector);
  const dispatch = useStoreDispatch();

  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 500);

  const [exportDisconnectedRadioNodes, exportStatus] = useExportDisconnectedRadioNodesMutation();
  const [rowClicked, setRowClicked] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<IEvent>();
  const [sortOrder, setSortOrder] = useState("occuredDateAndTimeMs,desc");
  const [sorting, setSorting] = useState<SortingState>([{ id: "occuredDateAndTimeMs", desc: true }]);

  const paginationModel = useSelector(paginationModelSelector);
  const pagination =
    paginationModel.disconnectedRadioNodesAlarms || {
      page: 0,
      pageSize: 10,
    };
  const [getDeviceFullDetail] = useGetDeviceFullDetailMutation();
  const [getDrawerCounts] = useGetDrawerCountsMutation();
  const [getAlarmsByDeviceKey] = useGetAlarmsByDeviceKeyMutation();
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const handleRowClicked = async (params: any) => {
      if (
        params.row.modelName === "migration" ||
        params.row.modelName === "unknown"
      )
        return;
  
      dispatch(
        setIsLoadingStatus({
          isLoadingDeviceDetail: true,
          isLoadingAlarms: true,
          isLoadingCounts: true,
        })
      );
  
      setRowClicked(true);
      dispatch(setOpenDeviceDrawer(true));
      setSelectedRowData(params.row);
  
      try {
        const args = {
          filters: `key=objectKey&value=${params.row.serialNumber}&weightType=FULL`,
          deviceKey: params.row.serialNumber,
        };
  
        const deviceDataResponse: any =
          await getDeviceFullDetail(args);
  
        if ("error" in deviceDataResponse)
          throw deviceDataResponse.error;
  
        if (deviceDataResponse?.data?.data?.[0]) {
          const fullDeviceData = {
            ...deviceDataResponse.data.data[0],
            ...deviceDataResponse.data?.deviceParams,
          };
          console.log("fullDeviceData",fullDeviceData)
          dispatch(setSelectedDevice(fullDeviceData));
          setSelectedRowData(fullDeviceData);
          dispatch(
            setIsLoadingStatus({
              isLoadingDeviceDetail: false,
            })
          );

          const resolvedObjectKey = fullDeviceData.objectKey;
  
          const configCountResponse: any =
            await getDrawerCounts({
              deviceKey: resolvedObjectKey,
              filters: "",
            });
  
          if ("error" in configCountResponse)
            throw configCountResponse.error;
  
          dispatch(setDrawerCounts(configCountResponse.data));
          dispatch(
            setIsLoadingStatus({
              isLoadingCounts: false,
            })
          );
  
          if (permissions?.hasOpCodeGroup("view_events")) {
            const alarmDataResponse: any =
              await getAlarmsByDeviceKey({
                deviceKey: resolvedObjectKey,
                filters:
                  "?sort=retention%2Cdesc&page=0&size=50",
              });
  
            if ("error" in alarmDataResponse)
              throw alarmDataResponse.error;
  
            dispatch(
              setSelectedDeviceAlarms(
                alarmDataResponse.data?.data
              )
            );
            dispatch(
              setSelectedDeviceAlarmCount(
                alarmDataResponse.data?.totalItems || 0
              )
            );
            dispatch(
              setIsLoadingStatus({
                isLoadingAlarms: false,
              })
            );
          }
        }
      } catch (error: any) {
        dispatch(
          setIsLoadingStatus({
            isLoadingDeviceDetail: false,
            isLoadingAlarms: false,
          })
        );
        dispatch(setOpenDeviceDrawer(false));
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "error",
            message: errorMessage(error),
          })
        );
      }
    };

  const geoRegionKey = selectedGeoRegions.join(',');

  useEffect(() => {
    if (pagination.page !== 0) {
      dispatch(setPaginationModel({ disconnectedRadioNodesAlarms: { ...pagination, page: 0 } }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoRegionKey]);

  const args = useMemo(() => {

    const params = new URLSearchParams();

    if (selectedGeoRegions.length > 0) {
      params.set("geoRegion", selectedGeoRegions.join(","));
    }
    params.set("search", debouncedSearch);
    params.set("sort", sortOrder);
    params.set("page", pagination.page.toString());
    params.set("size", pagination.pageSize.toString());

    return { filters: params.toString() };
  }, [keyword, debouncedSearch, selectedGeoRegions, sortOrder, pagination.page, pagination.pageSize]);

  const { data, isLoading, refetch } = useGetRadioNodeQuery(args, {
    pollingInterval:
      API.ENDPOINTS.ALARMS.pollingInterval || API.POLLING_INTERVAL,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    refetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoRegionKey]);
  const rowsData: Array<{ disconnectedRadioNodes?: number }> =
    (data?.content as Array<{ disconnectedRadioNodes?: number }>) || [];
  const totalCount = data?.totalElements || 0;
  const totalDisconnectedRadioNodes = rowsData.reduce(
    (sum: number, row: any) => sum + (Number(row?.disconnectedRadioNodes) || 0),
    0
  );

  const exportHandler = async () => {
    try {
      const response: any = await exportDisconnectedRadioNodes(args);
      if (response?.error) throw response?.error;
      exportInCSV(response, "DisconnectedRadioNodes");
    } catch (error: any) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: errorMessage(error),
        })
      );
    }
  };

  const handleCloseButtonClicked = (value: boolean) => {
    setRowClicked(value);
  };

  return (
    <>
      {rowClicked && selectedRowData && (
        <DeviceDrawer
          data={selectedRowData as any}
          closedButtonClicked={handleCloseButtonClicked}
        />
      )}
      <div className="w-full">
        {isLoading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <CircularProgress size={24} />
          </div>
        ) : (
          <>
            <h4 className="text-2xl font-bold my-0" style={{ color: "var(--foreground)" }}>{totalDisconnectedRadioNodes}</h4>

            <div className="mt-4 space-y-4">
              <DataGridToolbar
                searchText={searchText}
                setSearchText={setSearchText}
                exportHandler={exportHandler}
                exportLoading={exportStatus.isLoading}
              />

              <CustomDataGrid
                  tableId="disconnectedRadioNodesAlarms"
                  rows={rowsData}
                  columns={disconnectedRadioNodeColumns}
                  rowCount={totalCount}
                  loading={isLoading}
                  disableColumnMenu
                  getRowId={(row: any) => row.name}
                  paginationMode="server"
                  onRowClick={handleRowClicked}
                  onSortModelChange={(params: GridSortModel) =>
                    setSortOrder(getSortBy(params))
                  }
                  sx={{ height: 600 }}
                />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default DisconnectedRadioNode;
